import React, { useState, useEffect } from 'react';
import PageContainer from '../../components/PageContainer';
import apiService from '../../services/apiService';
import { useUser } from '../../contexts/UserContext';
import { twClasses, combineClasses } from '../../styles/tailwind-utils';
import { Navigate } from 'react-router-dom';
import { LoadingState } from '../../components/LoadingState';
import {
  Typography,
  Box,
  Grid,
  Paper,
  CircularProgress,
  Chip,
  TextField,
  Button,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Skeleton,
  Divider,
  Modal,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Checkbox,
  IconButton,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Close as CloseIcon } from '@mui/icons-material';
import { getRoleDisplayName } from '../../utils/roleUtils';

interface Client {
  id: number;
  agency_name: string;
  url?: string;
}

interface ClientAssignment {
  client_id: number;
  role: string;
  client: Client;
  created_at?: string;
  updated_at?: string;
}

interface User {
  id: number;
  username: string;
  email: string | null;
  is_admin?: boolean;
  is_tools_user?: boolean;
  last_login: string | null;
  created_at: string | null;
  updated_at: string | null;
  client_assignments: ClientAssignment[];
}

// Create new types for the form data
interface UserFormData {
  id: number;
  username: string;
  email: string;
  is_admin: boolean;
  is_tools_user: boolean;
  client_assignments: ClientAssignment[];
}

interface CognitoProfile extends Record<string, any> {
  'cognito:groups'?: string[];
  email?: string;
}

const Users: React.FC = () => {
  const auth = useUser();
  const userProfile = auth.user?.profile as CognitoProfile;
  const { isAdmin } = useUser();
  const [users, setUsers] = useState<User[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Add state for selected user to edit
  const [selectedUser, setSelectedUser] = useState<UserFormData | null>(null);
  const [originalAssignments, setOriginalAssignments] = useState<ClientAssignment[]>([]);
  const [originalIsToolsUser, setOriginalIsToolsUser] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [availableClients, setAvailableClients] = useState<Client[]>([]);
  const [showAddClient, setShowAddClient] = useState<boolean>(false);
  const [newClientId, setNewClientId] = useState<number | ''>('');
  const [newClientRole, setNewClientRole] = useState<string>('ic');

  // Load selected client ID from localStorage on mount and when auth changes
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);

  // Add new state for selected users
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);

  // Add auto-dismiss effect for success messages
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  // Add auto-dismiss effect for error messages
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Add useEffect to track changes
  useEffect(() => {
    if (selectedUser && originalAssignments) {
      // Compare current assignments with original
      const currentAssignments = selectedUser.client_assignments;

      // is_tools_user changed?
      if (selectedUser.is_tools_user !== originalIsToolsUser) {
        setHasChanges(true);
        return;
      }

      // Check if arrays have different lengths
      if (currentAssignments.length !== originalAssignments.length) {
        setHasChanges(true);
        return;
      }

      // Check if any assignments are different
      const hasChanges = currentAssignments.some(current => {
        const original = originalAssignments.find(o => o.client_id === current.client_id);
        return !original || original.role !== current.role;
      });

      setHasChanges(hasChanges);
    }
  }, [selectedUser?.client_assignments, selectedUser?.is_tools_user, originalAssignments, originalIsToolsUser]);

  // Fetch users and clients on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const [usersResponse, clientsResponse] = await Promise.all([
          apiService.get<User[]>('/api/admin/users'),
          apiService.get<Client[]>('/api/admin/clients')
        ]);

        setUsers(usersResponse.data as User[]);
        setClients(clientsResponse.data as Client[]);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Load selected client ID from localStorage on mount and when auth changes
  useEffect(() => {
    if (auth.isAuthenticated) {
      const storedClientId = localStorage.getItem('selectedClientId');
      if (storedClientId) {
        setSelectedClientId(parseInt(storedClientId, 10));
      }
    }
  }, [auth.isAuthenticated]);

  const handleEditUser = (user: User) => {
    // Transform user object to form data
    const formData: UserFormData = {
      id: user.id,
      username: user.username,
      email: user.email || '',
      is_admin: !!user.is_admin,
      is_tools_user: !!user.is_tools_user,
      client_assignments: user.client_assignments || []
    };

    setSelectedUser(formData);
    setOriginalAssignments(user.client_assignments || []);
    setOriginalIsToolsUser(!!user.is_tools_user);
    setHasChanges(false);

    // Calculate available clients (all clients minus already assigned ones)
    const assignedClientIds = new Set((user.client_assignments || []).map(a => a.client_id));
    const available = clients.filter(client => !assignedClientIds.has(client.id));
    setAvailableClients(available);

    setNewClientId('');
    setNewClientRole('ic');
  };

  const handleCloseEdit = () => {
    setSelectedUser(null);
    setOriginalAssignments([]);
    setHasChanges(false);
    setShowAddClient(false);
  };

  const handleRemoveAssignment = (clientId: number) => {
    if (!selectedUser) return;

    setSelectedUser({
      ...selectedUser,
      client_assignments: selectedUser.client_assignments.filter(
        assignment => assignment.client_id !== clientId
      )
    });

    // Update available clients
    const removedClient = clients.find(c => c.id === clientId);
    if (removedClient) {
      setAvailableClients([...availableClients, removedClient]);
    }
  };

  const handleToggleAddClient = () => {
    setShowAddClient(!showAddClient);
  };

  const handleAddClientAssignment = () => {
    if (!selectedUser || !newClientId) return;

    const clientToAdd = clients.find(c => c.id === newClientId);
    if (!clientToAdd) return;

    // Add client to assignments
    const newAssignment: ClientAssignment = {
      client_id: Number(newClientId),
      role: newClientRole,
      client: {
        id: clientToAdd.id,
        agency_name: clientToAdd.agency_name,
        url: clientToAdd.url
      }
    };

    setSelectedUser({
      ...selectedUser,
      client_assignments: [...selectedUser.client_assignments, newAssignment]
    });

    // Remove from available clients
    setAvailableClients(availableClients.filter(c => c.id !== Number(newClientId)));

    // Reset form
    setNewClientId('');
    setShowAddClient(false);
  };

  const handleSaveUser = async () => {
    if (!selectedUser) return;

    try {
      setLoading(true);

      // Format the update payload
      const updatePayload = {
        username: selectedUser.username,
        email: selectedUser.email,
        is_tools_user: selectedUser.is_tools_user,
        client_assignments: selectedUser.client_assignments.map(assignment => ({
          client_id: assignment.client_id,
          role: assignment.role
        }))
      };

      // Send update request
      await apiService.put<User>(`/api/admin/users/${selectedUser.id}`, updatePayload);

      // Refresh users data
      const updatedUsersResponse = await apiService.get<User[]>('/api/admin/users');
      setUsers(updatedUsersResponse.data as User[]);

      setSuccess('User updated successfully');
      setSelectedUser(null);
    } catch (err) {
      console.error('Error updating user:', err);
      setError('Failed to update user. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Find client name by ID
  const getClientName = (clientId: number): string => {
    const client = clients.find(c => c.id === clientId);
    return client ? client.agency_name : `Client ${clientId}`;
  };

  // Add handler for selecting all users
  const handleSelectAllUsers = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedUsers(users.map(user => user.id));
    } else {
      setSelectedUsers([]);
    }
  };

  // Add handler for selecting individual user
  const handleSelectUser = (userId: number) => {
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  // Add handler for deleting selected users
  const handleDeleteSelectedUsers = async () => {
    try {
      setLoading(true);
      await apiService.post('/api/admin/users/delete', { ids: selectedUsers });

      // Refresh users list
      const response = await apiService.get<User[]>('/api/admin/users');
      setUsers(response.data);
      setSelectedUsers([]);
      setSuccess('Users deleted successfully');
    } catch (err) {
      console.error('Error deleting users:', err);
      setError('Failed to delete users');
    } finally {
      setLoading(false);
    }
  };

  // Add handler for removing client assignment
  const handleRemoveClientAssignment = async (userId: number, clientId: number) => {
    try {
      const user = users.find(u => u.id === userId);
      if (!user || !user.client_assignments) return;

      // Update UI immediately
      const updatedAssignments = user.client_assignments.filter(
        a => a.client_id !== clientId
      );
      setUsers(users.map(u =>
        u.id === userId
          ? { ...u, client_assignments: updatedAssignments }
          : u
      ));

      // Make API call in background
      await apiService.put(`/api/admin/users/${userId}`, {
        ...user,
        client_assignments: updatedAssignments
      });

      setSuccess('Client assignment removed successfully');
    } catch (err) {
      console.error('Error removing client assignment:', err);
      setError('Failed to remove client assignment');

      // Revert UI on error
      const response = await apiService.get<User[]>('/api/admin/users');
      setUsers(response.data);
    }
  };

  // Use isAdmin() for admin check
  if (!isAdmin()) {
    return <Navigate to="/" />;
  }

  // Render a table of users with their client assignments
  return (
    <PageContainer>
      <Typography
        variant="subtitle1"
        color="text.secondary"
        sx={{ mb: 3 }}
      >
        User Management
      </Typography>

      <Paper elevation={2} className="p-6">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          {selectedUsers.length > 0 && (
            <Button
              variant="contained"
              color="error"
              onClick={handleDeleteSelectedUsers}
              startIcon={<DeleteIcon />}
            >
              Delete Selected ({selectedUsers.length})
            </Button>
          )}
        </Box>

        {/* Alerts */}
        {error && (
          <div className={combineClasses(
            twClasses.alert,
            twClasses.alertError,
            "mb-4",
            "transition-opacity duration-500 ease-in-out"
          )}>
            <p>{error}</p>
          </div>
        )}

        {success && (
          <div className={combineClasses(
            twClasses.alert,
            twClasses.alertSuccess,
            "mb-4",
            "transition-opacity duration-500 ease-in-out"
          )}>
            <p>{success}</p>
          </div>
        )}

        {/* Loading indicator */}
        {loading && (
          <LoadingState message="Loading users..." />
        )}

        {!loading && (
          <TableContainer component={Paper} elevation={0}>
            <Table size="medium">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={selectedUsers.length > 0 && selectedUsers.length < users.length}
                      checked={selectedUsers.length === users.length && users.length > 0}
                      onChange={handleSelectAllUsers}
                    />
                  </TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Client Assignments</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map(user => (
                  <TableRow key={user.id} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedUsers.includes(user.id)}
                        onChange={() => handleSelectUser(user.id)}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {user.username}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {user.email || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, flex: 1 }}>
                          {user.client_assignments && user.client_assignments.length > 0 ? (
                            user.client_assignments.map(assignment => (
                              <Chip
                                key={assignment.client_id}
                                label={
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Typography variant="caption" sx={{ mr: 0.5, color: 'white' }}>
                                      {assignment.client.agency_name}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: 'white', opacity: 0.8 }}>
                                      ({getRoleDisplayName(assignment.role)})
                                    </Typography>
                                  </Box>
                                }
                                onDelete={() => handleRemoveClientAssignment(user.id, assignment.client_id)}
                                size="small"
                                sx={{
                                  backgroundColor: 'primary.main',
                                  color: 'white',
                                  '& .MuiChip-deleteIcon': {
                                    color: 'white',
                                    opacity: 0.8,
                                    '&:hover': {
                                      opacity: 1
                                    }
                                  }
                                }}
                              />
                            ))
                          ) : (
                            <Typography variant="body2" color="text.secondary" fontStyle="italic">
                              No assignments
                            </Typography>
                          )}
                        </Box>
                        <Tooltip title={user.client_assignments?.length > 0 ? "Edit Assignments" : "Add Assignments"}>
                          <IconButton
                            size="small"
                            onClick={() => handleEditUser(user)}
                            sx={{ ml: 1 }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Edit user modal */}
      {selectedUser && (
        <Dialog
          open={true}
          onClose={handleCloseEdit}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Edit User Assignments
            <IconButton
              aria-label="close"
              onClick={handleCloseEdit}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Username:</strong> {selectedUser.username}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Email:</strong> {selectedUser.email}
              </Typography>
              <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Checkbox
                    checked={selectedUser.is_tools_user}
                    disabled={selectedUser.is_admin}
                    onChange={(e) =>
                      setSelectedUser({ ...selectedUser, is_tools_user: e.target.checked })
                    }
                  />
                  <Typography variant="body2">
                    Tools user
                    {selectedUser.is_admin && (
                      <Typography component="span" variant="caption" sx={{ ml: 0.5, color: 'text.secondary' }}>
                        (admins always have tools access)
                      </Typography>
                    )}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Table size="small" sx={{ mt: 3 }}>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox />
                  </TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Role</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {clients.map(client => {
                  const assignment = selectedUser.client_assignments.find(
                    a => a.client_id === client.id
                  );
                  return (
                    <TableRow key={client.id}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={!!assignment}
                          onChange={() => {
                            if (assignment) {
                              handleRemoveAssignment(client.id);
                            } else {
                              // Add new assignment directly without using newClientId state
                              const newAssignment: ClientAssignment = {
                                client_id: client.id,
                                role: 'ic',
                                client: {
                                  id: client.id,
                                  agency_name: client.agency_name,
                                  url: client.url
                                }
                              };

                              setSelectedUser({
                                ...selectedUser,
                                client_assignments: [...selectedUser.client_assignments, newAssignment]
                              });
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell>{client.agency_name}</TableCell>
                      <TableCell>
                        <ToggleButtonGroup
                          value={assignment?.role || 'ic'}
                          exclusive
                          onChange={(e, newRole) => {
                            if (newRole !== null && assignment) {
                              setSelectedUser({
                                ...selectedUser,
                                client_assignments: selectedUser.client_assignments.map(a =>
                                  a.client_id === client.id ? { ...a, role: newRole } : a
                                )
                              });
                            }
                          }}
                          disabled={!assignment}
                          size="small"
                          sx={{
                            opacity: assignment ? 1 : 0.5,
                            '& .MuiToggleButton-root': {
                              '&.Mui-disabled': {
                                color: 'text.disabled',
                                borderColor: 'rgba(0, 0, 0, 0.12)'
                              }
                            }
                          }}
                        >
                          <ToggleButton
                            value="ic"
                            sx={{
                              px: 2,
                              py: 0.5,
                              '&.Mui-selected': {
                                backgroundColor: 'primary.main',
                                color: 'white',
                                '&:hover': {
                                  backgroundColor: 'primary.dark',
                                  color: 'white'
                                }
                              },
                              '&.Mui-disabled': {
                                backgroundColor: 'transparent'
                              }
                            }}
                          >
                            Viewer
                          </ToggleButton>
                          <ToggleButton
                            value="owner"
                            sx={{
                              px: 2,
                              py: 0.5,
                              '&.Mui-selected': {
                                backgroundColor: 'primary.main',
                                color: 'white',
                                '&:hover': {
                                  backgroundColor: 'primary.dark',
                                  color: 'white'
                                }
                              },
                              '&.Mui-disabled': {
                                backgroundColor: 'transparent'
                              }
                            }}
                          >
                            OWNER
                          </ToggleButton>
                        </ToggleButtonGroup>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseEdit}>Cancel</Button>
            <Button
              onClick={handleSaveUser}
              variant="contained"
              color="primary"
              disabled={!hasChanges}
            >
              Save Changes
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </PageContainer>
  );
};

export default Users;
